import { Component, ViewChild } from '@angular/core';
import { SlickDialogComponent } from "slick-components";
import { HttpErrorDialogService } from "@services/utils/http-error-dialog.service";
import { HttpErrorResponse } from "@angular/common/http";
import { GlobalsService } from '@services';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';

@Component({
    selector: 'http-error-dialog',
    template: `
<slick-dialog key='DIALOG_HTTP_ERROR' width='900' #dialogRef header="{{header}}" (onClose)="onHttpErrorClose()">
    <div *ngIf='!isOnlinePayments' class='ps-3 d-flex flex-column' style=' font-family: "Nunito", sans-serif'>
        <div style='font-size: 1.1em;' class='text-danger d-flex mb-2'>
            <div class='mt-2 mx-2'>
                <img src='https://docs.jobsight.com/pc-error.png' />
            </div>
            <div class='mt-4 ms-3' style='font-size: 22px; line-height: 24px'>
                <div class='text-danger' style='font-size: 50px; font-weight: bold; margin-bottom: 30px'>ruh roh!</div>
                <div class='mt-3'>Pancake says she's sorry, but something went wrong.</div>
                <div class="my-2 bg-danger text-white p-3" style="font-size: 18px; line-height: 20px">
                    <span *ngIf="errorMessage">{{ errorMessage }}<br /></span>
                    {{ exceptionMessage }}
		        </div>
                <div class='mt-3'>Try your request again, and if it still isn't working, please reach out to customer support.</div>
				<div class='mt-4' style='font-size: 16px; line-height: 20px'>
					<a href='https://forms.zohopublic.com/jobsight1/form/Contactforsupport/formperma/QJkD52MQeyERlCFVttsy5cN72LiT1B_9wjS1mqO9TRg' target="_blank">Click here to submit a support ticket</a>
                </div>
            </div>
        </div>
        <div *ngIf='stackTrace && showStackTrace === false' style='font-size: .8rem;'><button class='btn btn-link m-0 p-0' (click)='showStackTrace = true;'><i class='far fa-plus-square'></i> Show Stack Trace</button> </div>
        <div *ngIf='stackTrace && showStackTrace === true' style='font-size: .8rem;' [innerHtml]='stackTrace'></div>
    </div>

	<div *ngIf='isOnlinePayments'>
		<div class='ps-3'>
		<div style='font-size: 1.1em;' class='text-danger'>
			<span *ngIf='errorMessage'>{{errorMessage}}<br /></span>
			{{exceptionMessage}}
		</div>
		<div *ngIf='stackTrace && showStackTrace === false' style='font-size: .8rem;'><button class='btn btn-link m-0 p-0' (click)='showStackTrace = true;'><i class='far fa-plus-square'></i> Show Stack Trace</button> </div>
		<div *ngIf='stackTrace && showStackTrace === true' style='font-size: .8rem;' [innerHtml]='stackTrace'></div>
	</div>
	</div>
</slick-dialog>`,
    standalone: false
})
export class HttpErrorDialogComponent {
    @ViewChild("dialogRef", { static: true }) dialogRef: SlickDialogComponent;

    constructor(private router: Router) {
        HttpErrorDialogService.errorDialogComponentCallback = this;
    }

    isOpen: boolean = false;
    header: string;
    errorMessage: string;
    exceptionMessage: string;
    showStackTrace: boolean = false;
    stackTrace: string;
    isOnlinePayments: boolean = GlobalsService.isOnlinePayments;
    isInvalidGrant: boolean = false;

    showHttpError(errorRes: HttpErrorResponse) {
        if (this.isOpen) return;

        this.isOpen = true;
        this.isInvalidGrant = false;
        this.showStackTrace = false;

        const errorContent = JSON.stringify(errorRes.error);
        // Every OAuth provider (Google/Outlook calendar, Zoho, ...) reports a dead refresh token as
        // invalid_grant, so only treat it as a QuickBooks login problem when it's QB's own exception,
        // which is thrown with exactly that message.
        const isQuickbooksInvalidGrant = errorContent.includes('"exceptionMessage":"invalid_grant"');

        if (errorRes.status === 404) {
            this.header = "Page not found";
            this.errorMessage = null;
            this.exceptionMessage = errorRes.url;
            this.stackTrace = null;
        } else if (errorRes.status === 500) {
            this.header = "An error has occurred";
            const actualError = errorRes.error.innerException || errorRes.error;
            this.errorMessage = (actualError.message === 'An error has occurred.') ? '' : actualError.message;
            this.exceptionMessage = actualError.exceptionMessage || actualError.message;
            this.stackTrace = errorRes.error.stackTrace
                ? errorRes.error.stackTrace.replace(/(?:\r\n|\r|\n)/g, '<br />')
                : "";
        } else {
            this.header = "An error has occurred";
            this.errorMessage = errorRes.message;
            this.exceptionMessage = errorContent;
        }

        if (
            isQuickbooksInvalidGrant ||
            errorContent.includes("already exists") ||
            errorContent.includes("clocked into") ||
            errorContent.includes("Please notify Dan.")
        ) {
            if (isQuickbooksInvalidGrant) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Oops',
                    html: `
                    Your QuickBooks login has expired.
                    Please log in again by clicking
                    <a href="javascript:void(0)" id="quickbooks-link" style="color: #007bff; text-decoration: underline; cursor: pointer;">here</a>
                `,
                    confirmButtonColor: '#007bff',
                    width: '28em',
                    heightAuto: false,
                    didOpen: () => {
                        const link = document.getElementById('quickbooks-link');
                        if (link) {
                            link.addEventListener('click', (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                Swal.close();
                                this.router.navigate(['/setup/quickbooksOnline']);
                            });
                        }
                    }
                }).then(() => this.isOpen = false);
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Oops',
                    text: this.exceptionMessage,
                    confirmButtonColor: '#007bff',
                    width: '28em',
                    heightAuto: false
                }).then(() => this.isOpen = false);
            }
        }
        else if (errorContent.includes("has not been synced to QuickBooks.")) {
            Swal.fire({
                icon: 'warning',
                title: 'Quickbooks sync error',
                text: this.exceptionMessage,
                confirmButtonColor: '#007bff',
                width: '28em',
                heightAuto: false
            }).then(() => this.isOpen = false);
        }
        else if (errorContent.includes("A folder with this name already exists for this job. Please choose a different name.")) {
            Swal.fire({
                icon: 'warning',
                title: 'Oops',
                text: "A folder with this name already exists for this job. Please choose a different name.",
                confirmButtonColor: '#007bff',
                width: '28em',
                heightAuto: false
            }).then(() => this.isOpen = false);
        }
        else if (errorContent.includes("Folder name cannot be empty")) {
            Swal.fire({
                icon: 'warning',
                title: 'Oops',
                text: "Folder name cannot be empty",
                confirmButtonColor: '#007bff',
                width: '28em',
                heightAuto: false
            }).then(() => this.isOpen = false);
        }
        else if (errorContent.includes("Folder name contains invalid characters.")) {
            Swal.fire({
                icon: 'warning',
                title: 'Folder name contains invalid characters.',
                text: "The following characters are not allowed: \\ / : * ? \" < > |",
                confirmButtonColor: '#007bff',
                width: '28em',
                heightAuto: false
            }).then(() => this.isOpen = false);
        }
        else {
            this.dialogRef.showDialog();
        }
    }

    onHttpErrorClose() {
        this.isOpen = false;
    }
}